import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  X,
  Phone,
  MessageCircle,
  MapPin,
  Navigation,
  CreditCard,
  ClipboardList,
  Contact,
  BadgeCheck,
  Settings
} from 'lucide-react';
import { stripHTML } from 'utils/helpers';
import { PORTAL_ROUTES, EXTERNAL_URLS, MEMBERSHIP_CHANGED } from 'utils/constants';
import { getPractice } from 'library/auth';
import { getMemberships } from 'store/Stripe/api';
import { isLiveEnrollment } from 'components/Profile/Insurance/Membership/helper';

interface SidebarProps {
  /** Called after a nav item is selected — used to close the drawer on mobile. */
  onNavigate?: () => void;
  /** Called by the mobile close (X) button. */
  onClose?: () => void;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

export const Sidebar = ({ onNavigate, onClose }: SidebarProps) => {
  const practiceInfo = getPractice();
  const [member, setMember] = useState(false);

  // Only members get the link — for everyone else the membership pitch stays
  // where it is, in the insurance step. A practice with no Stripe account errors
  // here, which is the same answer: no link.
  useEffect(() => {
    getMemberships()
      .then(({ enrollment }: any) => setMember(isLiveEnrollment(enrollment)))
      .catch(() => setMember(false));
  }, []);

  // Joining or cancelling happens in a tree of its own; without this the link
  // only catches up on a page reload.
  useEffect(() => {
    const onChange = (event: Event) => setMember(!!(event as CustomEvent).detail);

    window.addEventListener(MEMBERSHIP_CHANGED, onChange);
    return () => window.removeEventListener(MEMBERSHIP_CHANGED, onChange);
  }, []);

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: PORTAL_ROUTES.DASHBOARD },
    { label: 'My Appointments', icon: <CalendarDays size={20} />, path: PORTAL_ROUTES.APPOINTMENTS },
    { label: 'Conversations', icon: <MessageCircle size={20} />, path: PORTAL_ROUTES.CONVERSATIONS },
    { label: 'Treatment Plan', icon: <ClipboardList size={20} />, path: PORTAL_ROUTES.TREATMENT_PLANS },
    { label: 'Billing', icon: <CreditCard size={20} />, path: PORTAL_ROUTES.BILLING },
    ...(member
      ? [{ label: 'My Membership', icon: <BadgeCheck size={20} />, path: PORTAL_ROUTES.MEMBERSHIP }]
      : []),
    ...(practiceInfo?.patient_forms_access
      ? [{ label: 'Profile & Forms', icon: <Contact size={20} />, path: PORTAL_ROUTES.PROFILE }]
      : []),
    { label: 'Settings', icon: <Settings size={20} />, path: PORTAL_ROUTES.SETTINGS },
  ];

  return (
    <aside className="flex flex-col h-full w-[260px] min-w-[260px] max-w-[85vw] bg-white rounded-2xl shadow-sm border border-gray-100 overflow-y-auto px-4 py-2">
      {/* Mobile drawer header — hidden on desktop where the sidebar is inline */}
      <div className="md:hidden flex items-center justify-between h-14 px-1 mb-1 border-b border-gray-100">
        <span className="text-lg font-bold text-secondary-900">Menu</span>
        <button
          type="button"
          onClick={() => onClose?.()}
          aria-label="Close menu"
          className="w-8 h-8 flex items-center justify-center text-secondary-400 hover:text-secondary-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Main nav */}
      <nav className="space-y-1">
        {mainNavItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.path}
            onClick={() => onNavigate?.()}
            className={({ isActive }) =>
              `w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] font-medium transition-all duration-200 ${isActive
                ? 'bg-secondary-900 text-white shadow-md'
                : 'text-secondary-500 hover:bg-gray-100 hover:text-secondary-800'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Practice info card */}
      {practiceInfo && (
        <div className="mt-auto mb-4">
          <div className="bg-secondary-50 rounded-2xl p-4 space-y-3">
            <p className="text-sm font-bold text-secondary-800">Need help?</p>
            {practiceInfo.phone_number && (
              <a
                href={`tel:${practiceInfo.phone_number}`}
                className="flex items-center gap-2 text-xs text-secondary-500 hover:text-secondary-700 transition-colors"
              >
                <Phone size={14} />
                {practiceInfo.phone_number}
              </a>
            )}
            {practiceInfo.address && (
              <div className="flex items-start gap-2 text-xs text-secondary-500">
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span>{stripHTML(practiceInfo.address)}</span>
              </div>
            )}
            {practiceInfo.address && (
              <a
                href={`${EXTERNAL_URLS.GOOGLE_MAPS_SEARCH}?api=1&query=${encodeURIComponent(`${practiceInfo.title} ${stripHTML(practiceInfo.address)}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-secondary-900 text-white text-xs font-semibold py-2 rounded-xl hover:bg-secondary-800 transition-colors"
              >
                <Navigation size={14} />
                Get Directions
              </a>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};
